import { useState, useCallback, useEffect, useRef } from 'react'
import { apiFetch } from '@/lib/api'
import { useToast } from '@/components/ToastContainer'
import { Button, Badge, Spinner, Tabs } from '@/ui'
import styles from './SyncToProd.module.css'

interface TableInfo {
  key: string
  label: string
  group: 'template' | 'asset' | 'ai'
}

interface ImportResult {
  dryRun: boolean
  tables: Array<{ table: string; label: string; count: number }>
  s3: { copied: number; failed: number } | null
}

interface ExportData {
  exportedAt: string
  sourceBucket: string
  sourceWebUrl: string
  tables: Record<string, unknown[]>
  s3Keys: string[]
}

const GROUP_LABELS: Record<string, string> = {
  template: '템플릿/디자인',
  asset: '에셋',
  ai: 'AI 설정',
}

const TAB_ITEMS = [
  { key: 'export', label: 'Export (내보내기)' },
  { key: 'import', label: 'Import (가져오기)' },
]

export function SyncToProd() {
  const { addToast } = useToast()
  const [activeTab, setActiveTab] = useState('export')
  const [tables, setTables] = useState<TableInfo[]>([])
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [isLoadingTables, setIsLoadingTables] = useState(true)

  // Export 상태
  const [isExporting, setIsExporting] = useState(false)

  // Import 상태
  const [importFile, setImportFile] = useState<ExportData | null>(null)
  const [importFileName, setImportFileName] = useState('')
  const [copyS3, setCopyS3] = useState(true)
  const [syncMode, setSyncMode] = useState<'idle' | 'preview' | 'execute'>('idle')
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 테이블 목록 로드
  useEffect(() => {
    apiFetch<{ tables: TableInfo[] }>('/api/admin/sync-to-prod/tables')
      .then((data) => {
        setTables(data.tables)
        const defaults = new Set(data.tables.filter((t) => t.group !== 'ai').map((t) => t.key))
        setSelectedKeys(defaults)
      })
      .catch((e) => addToast({ type: 'error', message: e.message }))
      .finally(() => setIsLoadingTables(false))
  }, [addToast])

  const toggleKey = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const toggleGroup = useCallback(
    (group: string) => {
      const groupKeys = tables.filter((t) => t.group === group).map((t) => t.key)
      setSelectedKeys((prev) => {
        const next = new Set(prev)
        const allSelected = groupKeys.every((k) => next.has(k))
        for (const k of groupKeys) {
          if (allSelected) next.delete(k)
          else next.add(k)
        }
        return next
      })
    },
    [tables],
  )

  // ── Export ──

  const handleExport = useCallback(async () => {
    if (selectedKeys.size === 0) {
      addToast({ type: 'error', message: '내보낼 테이블을 선택해주세요.' })
      return
    }
    setIsExporting(true)
    try {
      const data = await apiFetch<ExportData>('/api/admin/sync-to-prod/export', {
        method: 'POST',
        body: JSON.stringify({ tables: [...selectedKeys] }),
      })

      // JSON 다운로드
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `fiket-export-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)

      const totalRows = Object.values(data.tables).reduce((sum, rows) => sum + rows.length, 0)
      addToast({ type: 'success', message: `${totalRows}건 데이터 + ${data.s3Keys.length}개 S3 키 내보내기 완료` })
    } catch (e: any) {
      addToast({ type: 'error', message: e.message })
    } finally {
      setIsExporting(false)
    }
  }, [selectedKeys, addToast])

  // ── Import ──

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      setImportFileName(file.name)
      setImportResult(null)

      const reader = new FileReader()
      reader.onload = (ev) => {
        try {
          const data = JSON.parse(ev.target?.result as string) as ExportData
          if (!data.tables) throw new Error('유효하지 않은 export 파일')
          setImportFile(data)
          addToast({ type: 'success', message: `${file.name} 로드 완료` })
        } catch (err: any) {
          addToast({ type: 'error', message: `파일 파싱 실패: ${err.message}` })
          setImportFile(null)
        }
      }
      reader.readAsText(file)
    },
    [addToast],
  )

  const handleImport = useCallback(
    async (dryRun: boolean) => {
      if (!importFile) return
      setSyncMode(dryRun ? 'preview' : 'execute')
      setImportResult(null)
      try {
        const body: Record<string, unknown> = {
          tables: importFile.tables,
          sourceWebUrl: importFile.sourceWebUrl,
          dryRun,
        }

        if (copyS3 && importFile.s3Keys.length > 0 && importFile.sourceBucket) {
          body.copyS3 = {
            fromBucket: importFile.sourceBucket,
            keys: importFile.s3Keys,
          }
        }

        const result = await apiFetch<ImportResult>('/api/admin/sync-to-prod/import', {
          method: 'POST',
          body: JSON.stringify(body),
        })
        setImportResult(result)
        if (!dryRun) {
          addToast({ type: 'success', message: '가져오기가 완료되었습니다.' })
        }
      } catch (e: any) {
        addToast({ type: 'error', message: e.message })
      } finally {
        setSyncMode('idle')
      }
    },
    [importFile, copyS3, addToast],
  )

  if (isLoadingTables) {
    return (
      <div className={styles.root}>
        <Spinner />
      </div>
    )
  }

  const groups = Object.entries(GROUP_LABELS).map(([group, label]) => ({
    group,
    label,
    items: tables.filter((t) => t.group === group),
  }))

  return (
    <div className={styles.root}>
      <h2 className={styles.title}>데이터 동기화</h2>
      <p className={styles.desc}>
        DB 데이터와 S3 파일을 JSON으로 내보내고 다른 환경에서 가져올 수 있습니다.
      </p>

      <Tabs items={TAB_ITEMS} value={activeTab} onChange={setActiveTab} variant="card" size="sm" />

      {/* ── Export 탭 ── */}
      {activeTab === 'export' && (
        <>
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>내보낼 테이블 선택</h3>
            {groups.map(({ group, label, items }) => {
              if (items.length === 0) return null
              const allSelected = items.every((t) => selectedKeys.has(t.key))
              return (
                <div key={group} className={styles.group}>
                  <label className={styles.groupHeader}>
                    <input type="checkbox" checked={allSelected} onChange={() => toggleGroup(group)} />
                    <strong>{label}</strong>
                  </label>
                  <div className={styles.checkboxList}>
                    {items.map((t) => (
                      <label key={t.key} className={styles.checkboxItem}>
                        <input
                          type="checkbox"
                          checked={selectedKeys.has(t.key)}
                          onChange={() => toggleKey(t.key)}
                        />
                        {t.label}
                      </label>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>

          <div className={styles.actions}>
            <Button variant="primary" onClick={handleExport} loading={isExporting}>
              JSON 다운로드
            </Button>
          </div>
        </>
      )}

      {/* ── Import 탭 ── */}
      {activeTab === 'import' && (
        <>
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>JSON 파일 선택</h3>
            <div className={styles.fileRow}>
              <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                파일 선택
              </Button>
              <span className={styles.fileName}>{importFileName || '선택된 파일 없음'}</span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            {importFile && (
              <>
                <div className={styles.fileMeta}>
                  <span>내보내기 시간: {new Date(importFile.exportedAt).toLocaleString()}</span>
                  <span>소스 버킷: {importFile.sourceBucket}</span>
                  <span>
                    테이블: {Object.keys(importFile.tables).length}개,
                    S3 키: {importFile.s3Keys.length}개
                  </span>
                </div>

                <label className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={copyS3}
                    onChange={(e) => setCopyS3(e.target.checked)}
                  />
                  <strong>S3 파일도 복사</strong>
                  <span className={styles.hint}>
                    ({importFile.sourceBucket} → 현재 버킷)
                  </span>
                </label>
              </>
            )}
          </div>

          {importFile && (
            <div className={styles.actions}>
              <Button
                variant="secondary"
                onClick={() => handleImport(true)}
                loading={syncMode === 'preview'}
                disabled={syncMode !== 'idle'}
              >
                미리보기 (dry-run)
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  if (window.confirm('현재 환경에 데이터를 가져옵니다. 계속하시겠습니까?')) {
                    handleImport(false)
                  }
                }}
                loading={syncMode === 'execute'}
                disabled={syncMode !== 'idle'}
              >
                Import 실행
              </Button>
            </div>
          )}

          {importResult && (
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>
                {importResult.dryRun ? '미리보기 결과' : 'Import 결과'}
                {importResult.dryRun ? (
                  <Badge color="yellow">dry-run</Badge>
                ) : (
                  <Badge color="green">완료</Badge>
                )}
              </h3>

              <table className={styles.resultTable}>
                <thead>
                  <tr>
                    <th>테이블</th>
                    <th>건수</th>
                  </tr>
                </thead>
                <tbody>
                  {importResult.tables.map((r) => (
                    <tr key={r.table}>
                      <td>{r.label}</td>
                      <td>{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {importResult.s3 && (
                <div className={styles.s3Result}>
                  S3: <strong>{importResult.s3.copied}</strong>개 파일 복사
                  {importResult.s3.failed > 0 && (
                    <>
                      , <span className={styles.warn}>{importResult.s3.failed}개 실패</span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
